import axios, { CancelTokenSource } from 'axios';

import { APIError, UnsupportedStructureError } from '~services/Errors';

import { GetMessageTemplatesResponseDecoder, MessageTemplateOmitId, MessageTemplatesResponse } from './domain';

export const getMessageTemplatesByNextUrl = async (
  nextUrl: string,
  cancelToken?: CancelTokenSource,
): Promise<MessageTemplatesResponse | undefined> => {
  const path = nextUrl;
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetMessageTemplatesResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getMessageTemplates = async (
  queue: string,
  search: string,
  filter: string,
  cancelToken?: CancelTokenSource,
): Promise<MessageTemplatesResponse | undefined> => {
  const path = `/api/async/queues/${queue}/message-templates`;
  const params = {
    search: search.toLowerCase() || undefined,
    filter: filter || undefined,
  };
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      params: params,
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetMessageTemplatesResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const deleteMessageTemplate = async (queue: string, messageTemplateId: number) => {
  const path = `/api/async/queues/${queue}/message-templates/${messageTemplateId}`;

  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const postMessageTemplate = async (queue: string, data: MessageTemplateOmitId): Promise<void> => {
  let path = `/api/async/queues/${queue}/message-templates`;

  const body = {
    category: data.category,
    title: data.title,
    template: data.template,
  };

  try {
    await axios.request({
      method: 'POST',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const putMessageTemplate = async (
  queue: string,
  messageTemplateId: number,
  data: MessageTemplateOmitId,
): Promise<void> => {
  let path = `/api/async/queues/${queue}/message-templates/${messageTemplateId}`;

  const body = {
    category: data.category,
    title: data.title,
    template: data.template,
  };

  try {
    await axios.request({
      method: 'PUT',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};
